import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import useLazyQuery from 'hook/useLazyQuery';
import { API_PATH } from 'common/constants/services';
import { useNavigate, useParams } from 'react-router';
import { AxiosError, AxiosResponse } from 'axios';
import { useChallengeContextContext } from 'context/ChallengeContext/ChallengeContext';
import useMutation, { METHOD } from 'hook/useMutation';
import { toast } from 'react-toastify';
import { Button, Stack } from '@mui/material';
import { validationRoutineRow } from 'utils/validationRoutineRow';
import Loading from 'components/Loading';
import { CSVLink, CSVDownload } from 'react-csv';
import convertCsvFormat from 'utils/convertCsvFormat';
import { routineCsvHeaders } from 'common/constants/routineCsvHeader';
import { format } from 'date-fns';
import { csvToJson } from 'utils/csvToJson';
import { validationCsvSameChallengeId } from 'utils/validationCsvSameChallengeId';
import WorkoutInsertModal from 'components/WorkoutInsertModal';
import {
  setServerRoutine,
  setNewRoutine,
  setServerToLocalRoutine,
} from 'utils/setRoutine';
import { changeArrayOrder } from 'utils/changeOrder';
import ChallengeTable from './ChallengeTable';
import RoutineErrorModal from '../RoutineErrorModal';

interface IProps {
  challengeId: string | undefined;
}

const defaultRoutine = {
  name: '',
  part: '',
  date: 0,
  weight_multiplier: 0,
  count_placeholder: '',
  rir: null,
  weight_placeholder: '',
  body_weight_multiplier: 0,
  weight_kg: 0,
  weight_lbs: 0,
  count: 0,
  set_count: 0,
  alt_workouts_part: null,
  alt_workouts_part_id: null,
  tip: null,
  rest_time_sec: null,
};

export function ChallengeRoutinesContents({ challengeId }: IProps) {
  const navigate = useNavigate();
  const {
    setChallengeById,
    setWorkoutAndConditions,
    workoutsAutoComplete,
    conditions,
    totalDate,
    refetchWorkout,
  } = useChallengeContextContext();
  const [routines, setRoutines] = useState<any>([]);
  const [defaultExpendedGroup, setDefaultExpendedGroup] = useState<string[]>(
    [],
  );
  const [error, setError] = useState<IError>({
    isOpen: false,
    message: '',
    status: undefined,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const [isSaveApiCalled, toggleSaveApiCalled] = useState(false);
  const [openWorkoutInsertModal, toggleOpenWorkoutInsertModal] =
    useState(false);

  const setRoutinesByRemote = useCallback(
    (data: IChallengeById, isRest: boolean) => {
      let result: IChallengeRoutines[] = [];
      const map = new Map();

      if (data.challenge_routines.length > 0) {
        data.challenge_routines
          .sort((a, b) => a.date - b.date)
          .reduce(
            (acc, cur) => {
              if (map.has(cur.date)) {
                const currentDateRoutine = map.get(cur.date);

                map.set(cur.date, [...currentDateRoutine, cur]);
              } else {
                map.set(cur.date, [cur]);
              }

              return acc;
            },
            { map },
          );
      }

      for (let i = 1; i < data.total_date + 1; i += 1) {
        if (map.has(i)) {
          const convertRoutine = map
            .get(i)
            .map((v: any) => setServerToLocalRoutine(v, workoutsAutoComplete));

          result = [...result, ...convertRoutine];
        } else {
          result = [
            ...result,
            cloneDeep({
              ...defaultRoutine,
              name: isRest ? 'isRest' : defaultRoutine.name,
              date: i,
              id: isRest ? `rest-${i}` : `default-${i}`,
              condition_id: null,
            }),
          ];
        }
      }

      const setDefaultExpanded = (routine: IChallengeRoutines[]) => {
        if (routine.length > 0) {
          return Array(routine.length)
            .fill(0)
            .map((_, idx) => String(idx + 1));
        }
        return [];
      };

      setRoutines(result);
      setDefaultExpendedGroup(setDefaultExpanded(result));
    },
    [setRoutines, workoutsAutoComplete],
  );

  const challengesById = useLazyQuery<IChallengeById>(
    `${API_PATH.CHALLENGES}${challengeId}`,
    {
      onSuccess: (res: AxiosResponse<IChallengeById, any>) => {
        const { data } = res;

        setChallengeById(data);
        setRoutinesByRemote(data, data.challenge_routines.length > 0);
      },
    },
  );

  const postChallengeRoutines = useMutation<IPostChallengeRoutines>(
    `${API_PATH.POST_CHALLENGES_ROUTINES_V2}/${challengeId}/routines`,
    {
      method: METHOD.POST,
      onSuccess: () => {
        toggleSaveApiCalled(false);
        toast.success('등록이 완료되었습니다.');
        challengesById.refetch();
        // navigate(`/challenges`);
      },
      onError: (error: AxiosError) => {
        const { response, message } = error;

        setError({
          isOpen: true,
          message,
          status: response?.status,
        });

        console.log(error);
      },
    },
  );

  useEffect(() => {
    if (challengeId && workoutsAutoComplete.length === 0) {
      setWorkoutAndConditions(Number(challengeId));
      return;
    }

    if (challengeId && !challengesById.data) {
      challengesById.refetch();
      setWorkoutAndConditions(Number(challengeId));
    }

    if (
      challengesById.data &&
      challengesById.data.data.challenge_routines.length > 0 &&
      routines.length === 0
    ) {
      setRoutinesByRemote(challengesById.data.data, true);
    }

    if (
      challengesById.data &&
      challengesById.data.data.challenge_routines.length === 0 &&
      routines.length === 0 &&
      challengesById.data.data.total_date > 0
    ) {
      setRoutinesByRemote(challengesById.data.data, false);

      if (workoutsAutoComplete.length === 0) {
        setWorkoutAndConditions(Number(challengeId));
      }
    }

    if (
      challengesById.data &&
      challengesById.data.data.challenge_conditions.length > 0 &&
      conditions.length === 0
    ) {
      setChallengeById(challengesById.data.data);
    }
  }, [
    challengeId,
    challengesById,
    setRoutinesByRemote,
    routines,
    setWorkoutAndConditions,
    workoutsAutoComplete,
    conditions,
    setChallengeById,
  ]);

  const convertMap = (data: IChallengeRoutines[]) => {
    const map = new Map();

    data.reduce(
      (acc, cur) => {
        if (map.has(cur.date)) {
          const currentDateRoutine = map.get(cur.date);

          map.set(cur.date, [...currentDateRoutine, cur]);
        } else {
          map.set(cur.date, [cur]);
        }

        return acc;
      },
      { map },
    );

    return map;
  };

  const onChangeRestDay = (date: number) => {
    const map = convertMap(routines);
    const isRest = map.get(date)[0].name === 'isRest';

    map.set(
      date,
      isRest
        ? { ...defaultRoutine, date, id: `default-${date}` }
        : {
            ...defaultRoutine,
            date,
            id: `rest-${date}`,
            name: 'isRest',
          },
    );

    const newRoutines = Array.from(map, ([_, value]) => value).flat();

    setRoutines(newRoutines);
  };

  const makeDuplicateRoutines = (
    duplicate: { day: number; checked: boolean; selectedDay: boolean }[],
    selectedDay: number,
    currentRoutines: IChallengeRoutines[],
  ) => {
    const map = convertMap(currentRoutines);
    const selectedRoutine = cloneDeep(
      routines.filter((item: IChallengeRoutines) => item.date === selectedDay),
    );

    duplicate.forEach((item) => {
      if (item.checked && !item.selectedDay) {
        map.set(
          item.day,
          cloneDeep(
            selectedRoutine.map((v: IChallengeRoutines, idx: number) => {
              const result = v;
              result.id = `duplicate-${v.date}-${idx}`;
              result.date = item.day;
              return result;
            }),
          ),
        );
      }
    });

    const newRoutines = Array.from(map, ([_, value]) => value).flat();

    setRoutines(newRoutines);
  };

  const newNextRowRoutine = (row: IChallengeRoutines) => {
    const index = routines.findIndex(
      (routine: IChallengeRoutines) => routine.id === row.id,
    );
    const newRoutine = [...routines];

    if (index >= 0) {
      newRoutine.splice(
        index + 1,
        0,
        cloneDeep({
          ...defaultRoutine,
          id: `new-default-row-${newRoutine.length}`,
          date: row.date,
        }),
      );

      setRoutines(newRoutine);
    }
  };

  const copyNextRowRoutine = (row: IChallengeRoutines) => {
    const index = routines.findIndex(
      (routine: IChallengeRoutines) => routine.id === row.id,
    );
    const newRoutine = [...routines];

    if (index >= 0) {
      newRoutine.splice(
        index + 1,
        0,
        cloneDeep({
          ...row,
          id: `copy-row-${newRoutine.length}`,
        }),
      );

      setRoutines(newRoutine);
    }
  };

  const deleteRowRoutine = (row: IChallengeRoutines) => {
    const newRoutine = [...routines].filter((routine: IChallengeRoutines) => {
      return routine.id !== row.id;
    });

    setRoutines(newRoutine);
  };

  const updateValue = (row: IChallengeRoutines, idx: number) => {
    const newRoutine = [...routines];

    newRoutine[idx] = row;

    setRoutines(newRoutine);
  };

  const onClickSave = () => {
    const newRoutine: IChallengeRoutines[] = [];
    let errorCount = 0;
    let curDay = 1;
    let dateRoutineRowNum = 0;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < routines.length; i++) {
      const curRoutine = routines[i];

      if (curRoutine.name !== 'isRest') {
        if (curRoutine.date === curDay) {
          dateRoutineRowNum += 1;
        } else {
          curDay = curRoutine.date;
          dateRoutineRowNum = 1;
        }
        const valid = validationRoutineRow(curRoutine, dateRoutineRowNum);

        if (valid.error) {
          errorCount += 1;
          setError({
            isOpen: true,
            message: valid.message,
            status: 9999,
          });
          break;
        }

        if (typeof curRoutine.id === 'string') {
          // count, set_count, weight_multiplier
          newRoutine.push(setNewRoutine(curRoutine, workoutsAutoComplete));
        } else {
          newRoutine.push(setServerRoutine(curRoutine, workoutsAutoComplete));
        }
      }
    }

    if (errorCount === 0) {
      toggleSaveApiCalled(true);
      postChallengeRoutines.mutate({
        // challenge_id: Number(challengeId),
        challenge_routines: newRoutine,
      });
    }
  };

  const isLoading = useMemo(() => challengesById.isLoading, [challengesById]);

  const onChangeCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    if (files && files.length > 0) {
      if (files[0]) {
        const fileReader = new FileReader();

        fileReader.onload = () => {
          if (!validationCsvSameChallengeId(fileReader.result, challengeId)) {
            alert('같은 챌린지의 데이터만 불러오기 가능합니다.');
            return;
          }

          const convert = csvToJson(fileReader.result, challengeId || '');

          if (challengesById.data?.data) {
            const data = {
              ...challengesById.data.data,
              challenge_routines: convert,
            };

            setRoutinesByRemote(data, convert.length > 0);
          }
        };

        fileReader.readAsText(files[0]);
      }
    }

    // input value 초기화 추가
    e.target.value = '';
  };

  const onClickImportCsv = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const onSuccessInsertWorkout = () => {
    refetchWorkout();
    toggleOpenWorkoutInsertModal(false);
  };

  const nextOrderRoutine = (row: IChallengeRoutines) => {
    const filterForRoutineDate = routines.filter(
      (routine: IChallengeRoutines) => routine.date === row.date,
    );
    const filterIndex = filterForRoutineDate.findIndex(
      (v: IChallengeRoutines) => v.id === row.id,
    );
    const index = routines.findIndex(
      (v: IChallengeRoutines) => v.id === row.id,
    );

    if (index >= 0 && filterIndex < filterForRoutineDate.length - 1) {
      setRoutines(changeArrayOrder(routines, index, 1));
    }
  };

  const prevOrderRoutine = (row: IChallengeRoutines) => {
    const filterForRoutineDate = routines.filter(
      (routine: IChallengeRoutines) => routine.date === row.date,
    );
    const filterIndex = filterForRoutineDate.findIndex(
      (v: IChallengeRoutines) => v.id === row.id,
    );
    const index = routines.findIndex(
      (v: IChallengeRoutines) => v.id === row.id,
    );
    const newRoutine = [...routines];

    if (index > 0 && filterIndex > 0) {
      setRoutines(changeArrayOrder(routines, index, -1));
    }
  };

  return (
    <div style={{ height: 'calc(100% - 118px)' }}>
      <RoutineErrorModal
        clearError={() =>
          setError({
            isOpen: false,
            message: '',
            status: undefined,
          })
        }
        error={error}
        routines={routines}
        fileName={`${challengesById.data?.data.name}_${format(
          new Date(),
          'yyyyMMdd-hhmmss',
        )}`}
        challengeId={challengeId}
      />
      {isSaveApiCalled && (
        <CSVDownload
          data={convertCsvFormat(routines, challengeId)}
          headers={routineCsvHeaders}
          filename={`${challengesById.data?.data.name}_${format(
            new Date(),
            'yyyyMMdd-hhmmss',
          )}`}
          target='_blank'
        />
      )}
      {openWorkoutInsertModal && (
        <WorkoutInsertModal
          isOpen={openWorkoutInsertModal}
          onSuccess={onSuccessInsertWorkout}
          close={() => toggleOpenWorkoutInsertModal(false)}
        />
      )}

      <Loading isLoading={isLoading} />
      <Stack
        direction='row'
        spacing={1}
        sx={{ justifyContent: 'flex-end', marginBottom: 2 }}
      >
        <Button
          variant='contained'
          size='small'
          onClick={() => toggleOpenWorkoutInsertModal(true)}
        >
          운동 등록
        </Button>
        <input
          type='file'
          accept='.csv'
          onChange={onChangeCsv}
          hidden
          ref={inputRef}
        />
        <Button variant='contained' size='small' onClick={onClickImportCsv}>
          데이터 등록
        </Button>
        <CSVLink
          className='download-button'
          data={convertCsvFormat(routines, challengeId)}
          headers={routineCsvHeaders}
          onClick={() => {
            console.log('download', routines);
          }}
          filename={`${challengesById.data?.data.name}_${format(
            new Date(),
            'yyyyMMdd-hhmmss',
          )}`}
        >
          데이터 다운로드
        </CSVLink>
      </Stack>
      <ChallengeTable
        routine={routines}
        defaultExpendedGroup={defaultExpendedGroup}
        newNextRowRoutine={newNextRowRoutine}
        copyNextRowRoutine={copyNextRowRoutine}
        deleteRowRoutine={deleteRowRoutine}
        updateValue={updateValue}
        onChangeRestDay={onChangeRestDay}
        makeDuplicateRoutines={makeDuplicateRoutines}
        totalDate={totalDate}
        nextOrderRoutine={nextOrderRoutine}
        prevOrderRoutine={prevOrderRoutine}
      />

      <Stack
        direction='row'
        sx={{
          gap: '0px 8px',
          width: '100%',
          marginTop: 2,
        }}
      >
        <Button
          variant='contained'
          color='info'
          fullWidth
          onClick={() =>
            navigate(`/challenges/modify/${challengeId}/conditions`)
          }
        >
          이전
        </Button>
        <Button variant='contained' fullWidth onClick={onClickSave}>
          저장(다음)
        </Button>
      </Stack>
    </div>
  );
}

export function ChallengeRoutines() {
  const param = useParams();

  return <ChallengeRoutinesContents challengeId={param.id} />;
}
